import React, { useState, useEffect } from 'react';
import {
  Grid, Box, TextField, FormControl, IconButton, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, Chip, Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { ACTIONS } from '../../../../shared/constants';
import { useSnackbar } from 'notistack';

const ManagementProfileDetail = ({
  data,
  handleClose,
  handleSave,
  profileId,
  profile,
  permissions,
  action
}) => {
  const [selectedprofileId] = useState(profileId);
  const [selectedCodes, setSelectedCodes] = useState(data || []);
  const [selectedCode, setSelectedCode] = useState(null);
  const isNameReadOnly = action !== ACTIONS.NEW;
  const [profileValue, setProfileValue] = useState(action === ACTIONS.NEW ? '' : profile);
  const { enqueueSnackbar } = useSnackbar();
  const [isDirty, setIsDirty] = useState(false);
  const [showCancelAlert, setShowCancelAlert] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  useEffect(() => {
    if (action === ACTIONS.NEW) {
      setSelectedCode(null);
      setSelectedCodes([]);
    }
  }, [action]);

  const handleAddPermission = (newValue) => {
    if (!newValue) return;

    let newSelectedCodes = [...selectedCodes];

    // Agrega menú padre automáticamente si es submenú
    if (newValue.menuType === 'Submenu' && newValue.parentCode) {
      const parentMenu = permissions.find(p => p.code === newValue.parentCode);
      if (parentMenu && !newSelectedCodes.some(c => c.code === parentMenu.code)) {
        newSelectedCodes.push({
          id: parentMenu.id,
          description: parentMenu.description,
          code: parentMenu.code,
          profileId: selectedprofileId,
          profileName: profileValue
        });
      }
    }

    if (newSelectedCodes.some(el => el.code === newValue.code)) return;

    newSelectedCodes.push({
      id: newValue.id,
      description: newValue.description,
      code: newValue.code,
      profileId: selectedprofileId,
      profileName: profileValue
    });

    setSelectedCodes(newSelectedCodes);
    setSelectedCode(newValue);
    setIsDirty(true);
    // Expande el grupo del permiso recién agregado
    const parentDef = permissions.find(p => p.code === newValue.parentCode);
    const groupName = parentDef?.description || newValue.description;
    setExpandedGroups(prev => new Set([...prev, groupName]));
  };

  const onSave = () => {
    if (profileValue.trim() === '') {
      enqueueSnackbar('El nombre del perfil es requerido', { variant: 'error' });
      return;
    }
    handleSave({
      permissions: selectedCodes.map(({ description, code, id }) => ({
        Description: description, Code: code, Id: id
      })),
      profileId: selectedprofileId,
      profileName: profileValue,
      action: selectedCodes.length > 0 ? 'MODIFICAR' : 'CREAR'
    });
  };

  const handleDeletePermission = (code) => {
    setSelectedCodes(prev => prev.filter(el => el.code !== code));
    setIsDirty(true);
  };

  // Agrupa permisos: los ítems de menú principal van dentro del grupo de sus hijos
  const groups = selectedCodes.reduce((acc, item) => {
    const permDef = permissions.find(p => p.code === item.code);
    const isMainMenu = permDef && permDef.menuType !== 'Submenu';

    if (isMainMenu) {
      // Va al grupo que lleva su propio nombre (sus hijos también lo referencian)
      const key = item.description;
      if (!acc[key]) acc[key] = { parent: null, children: [] };
      acc[key].parent = item;
    } else {
      // Submenu — busca su padre
      const parentDef = permissions.find(p => p.code === permDef?.parentCode);
      const key = parentDef?.description || 'Otros';
      if (!acc[key]) acc[key] = { parent: null, children: [] };
      acc[key].children.push(item);
    }
    return acc;
  }, {});

  return (
    <>
      <Dialog maxWidth="md" fullWidth open>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold" style={{ color: '#1a3072' }}>
            Detalle <span style={{ color: '#00b0ef' }}>Perfil</span>
          </Typography>
        </DialogTitle>

        <DialogContent>
          {/* Nombre del perfil */}
          <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f7fa', borderRadius: 2, border: '1px solid #e0e4ed' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
              Datos del Perfil
            </Typography>
            <TextField
              label="Nombre del perfil"
              fullWidth
              size="small"
              value={profileValue}
              onChange={(e) => { setProfileValue(e.target.value); setIsDirty(true); }}
              required
              InputProps={{ readOnly: isNameReadOnly }}
              variant="outlined"
            />
          </Box>

          {/* Permisos */}
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
            Asignación de Permisos
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <Autocomplete
              size="small"
              options={permissions.filter(p => p.menuType !== 'Menu')}
              groupBy={(option) => {
                const parent = permissions.find(p => p.code === option.parentCode) || {};
                return parent.description || 'Otros';
              }}
              getOptionLabel={(option) => option.description}
              value={selectedCode}
              onChange={(_, newValue) => {
                setSelectedCode(newValue);
                if (newValue) handleAddPermission(newValue);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Buscar permiso" variant="outlined" size="small" />
              )}
              renderGroup={(params) => (
                <li key={params.key}>
                  <Box sx={{
                    px: 2, py: 0.8,
                    bgcolor: '#f0f4ff',
                    borderBottom: '1px solid #e0e4ed',
                    borderTop: '1px solid #e0e4ed',
                    display: 'flex', alignItems: 'center', gap: 1,
                    position: 'sticky', top: -8, zIndex: 1,
                  }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#1a3072', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                      {params.group}
                    </Typography>
                  </Box>
                  <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                </li>
              )}
              renderOption={(props, option) => (
                <li {...props} style={{ paddingLeft: '20px', paddingTop: '4px', paddingBottom: '4px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SubdirectoryArrowRightIcon sx={{ fontSize: 14, color: '#00b0ef', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      {option.description}
                    </Typography>
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              noOptionsText="Sin resultados"
              ListboxProps={{ style: { maxHeight: 280 } }}
            />
          </FormControl>

          {selectedCodes.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center', color: '#aaa' }}>
              <Typography variant="body2">Sin permisos asignados</Typography>
            </Box>
          ) : (
            <>
              {/* Leyenda */}
              <Box sx={{ display: 'flex', gap: 2, mb: 1.5, px: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#1a3072' }} />
                  <Typography variant="caption" sx={{ color: '#555' }}>Menú principal</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#00b0ef' }} />
                  <Typography variant="caption" sx={{ color: '#555' }}>Submenú</Typography>
                </Box>
              </Box>

              <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                {Object.entries(groups).map(([groupName, { parent, children }]) => (
                  <Accordion key={groupName} expanded={expandedGroups.has(groupName)} disableGutters elevation={0}
                    onChange={(_, isExpanded) => setExpandedGroups(prev => { const next = new Set(prev); isExpanded ? next.add(groupName) : next.delete(groupName); return next; })}
                    sx={{ border: '1px solid #e0e4ed', borderRadius: '8px !important', mb: 1, '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}
                      sx={{ bgcolor: '#f0f4ff', borderRadius: '8px', minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: '#1a3072', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                            {groupName}
                          </Typography>
                          <Chip
                            label={(parent ? 1 : 0) + children.length}
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#1a3072', color: '#fff' }}
                          />
                        </Box>
                        <Chip
                          icon={<DeleteSweepIcon sx={{ fontSize: '14px !important' }} />}
                          label="Quitar todo"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            const codesToRemove = new Set([
                              ...(parent ? [parent.code] : []),
                              ...children.map(c => c.code)
                            ]);
                            setSelectedCodes(prev => prev.filter(el => !codesToRemove.has(el.code)));
                            setIsDirty(true);
                          }}
                          sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#fef2f2', color: '#e53935', border: '1px solid #fecaca', '& .MuiChip-icon': { color: '#e53935' }, '&:hover': { bgcolor: '#fee2e2' } }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 1, pb: 1.5 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {parent && (
                          <Chip
                            key={parent.code}
                            label={parent.description}
                            size="small"
                            onDelete={() => handleDeletePermission(parent.code)}
                            sx={{ bgcolor: '#1a3072', color: '#fff', fontWeight: 600, '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' } }}
                          />
                        )}
                        {children.map(p => (
                          <Chip
                            key={p.code}
                            label={p.description}
                            size="small"
                            onDelete={() => handleDeletePermission(p.code)}
                            sx={{ bgcolor: '#e0f4ff', color: '#00639b', fontWeight: 500, border: '1px solid #b3e0f7', '& .MuiChip-deleteIcon': { color: '#e53935' } }}
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ m: 1 }}>
          <Button color="primary" variant="contained" onClick={onSave} endIcon={<SaveIcon />}>
            {ACTIONS.SAVE}
          </Button>
          <Box sx={{ m: 1 }}>
            <Button endIcon={<CancelIcon />} variant="outlined"
              onClick={() => isDirty ? setShowCancelAlert(true) : handleClose()}>
              {ACTIONS.CANCEL}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={showCancelAlert} maxWidth="xs" fullWidth onClose={() => setShowCancelAlert(false)}>
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 56, color: '#f59e0b', mb: 1.5 }} />
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#1a3072', mb: 1 }}>¿Descartar cambios?</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>Tiene cambios sin guardar. Si sale ahora, se perderán todos los cambios realizados.</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 3 }}>
          <Button variant="outlined" onClick={() => setShowCancelAlert(false)}
            sx={{ minWidth: 140, borderColor: '#1a3072', color: '#1a3072', '&:hover': { borderColor: '#1a3072', bgcolor: '#f0f4ff' } }}>
            Seguir editando
          </Button>
          <Button variant="contained" onClick={() => { setShowCancelAlert(false); handleClose(); }}
            sx={{ minWidth: 140, bgcolor: '#e53935', '&:hover': { bgcolor: '#b71c1c' } }}>
            Salir sin guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManagementProfileDetail;
